import React, { useState, useEffect } from 'react';
import { Link, useParams } from 'react-router-dom';
import { Button } from '@/components/ui/button';
import { Card, CardContent } from '@/components/ui/card';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { Badge } from '@/components/ui/badge';
import { motion, AnimatePresence } from 'framer-motion';
import { useAuth } from '@/contexts/AuthContext';
import { toast } from 'sonner';
import { 
  ArrowLeft, BookOpen, Heart, Play, Star, Loader2, Search,
  Baby, GraduationCap, Award, ChevronRight, Book
} from 'lucide-react';
import { Input } from '@/components/ui/input';

// Emoji mapping for stories based on character/theme
const STORY_EMOJIS = {
  'creation': '🌍', 'adam-eve': '🍎', 'noah': '🚢', 'rainbow-promise': '🌈', 'tower-babel': '🗼',
  'abraham': '⭐', 'isaac': '👶', 'jacob': '🪜', 'joseph': '🧥', 'moses': '🔥', 'baby-moses': '👶',
  'red-sea': '🌊', 'ten-commandments': '📜', 'joshua': '🏰', 'samson': '💪', 'ruth': '🌾',
  'david': '👑', 'goliath': '⚔️', 'solomon': '💡', 'elijah': '🦅', 'elisha': '🫗', 'daniel': '🦁',
  'jonah': '🐋', 'esther': '👸', 'nehemiah': '🧱', 'job': '🙏', 'isaiah': '📢', 'jeremiah': '😢',
  'ezekiel': '💀', 'jesus': '✝️', 'christmas': '🎄', 'wise-men': '⭐', 'baptism': '💧',
  'miracle': '✨', 'parable': '📖', 'lazarus': '🪦', 'palm': '🌴', 'supper': '🍞',
  'cross': '✝️', 'resurrection': '🌅', 'pentecost': '🔥', 'paul': '⚡', 'saul': '👑',
  'deborah': '⚖️', 'gideon': '🔦', 'samuel': '👂', 'naaman': '🛁'
};

// Get emoji for a story
const getStoryEmoji = (storyId) => {
  for (const [key, emoji] of Object.entries(STORY_EMOJIS)) {
    if (storyId.toLowerCase().includes(key)) {
      return emoji;
    }
  }
  return '📖'; // Default
};

// Level configuration
const LEVEL_CONFIG = {
  1: { name: 'Little Learners', ages: '3-6', icon: Baby, color: 'from-pink-500 to-rose-500', bgColor: 'bg-pink-50' },
  2: { name: 'Growing Faith', ages: '7-9', icon: Book, color: 'from-purple-500 to-indigo-500', bgColor: 'bg-purple-50' },
  3: { name: 'Bible Explorers', ages: '10-12', icon: GraduationCap, color: 'from-blue-500 to-cyan-500', bgColor: 'bg-blue-50' }
};

const BibleStoriesPage = () => {
  const { childId } = useParams();
  const { api } = useAuth();
  const [stories, setStories] = useState({ 1: [], 2: [], 3: [] });
  const [chapters, setChapters] = useState([]);
  const [loading, setLoading] = useState(true);
  const [activeLevel, setActiveLevel] = useState('1');
  const [searchQuery, setSearchQuery] = useState('');
  const [showChapters, setShowChapters] = useState(false);

  useEffect(() => {
    fetchAllStories();
    fetchChapters();
  }, []);

  const fetchAllStories = async () => {
    setLoading(true);
    try {
      const results = await Promise.all([
        api.get('/bible/stories/1'),
        api.get('/bible/stories/2'),
        api.get('/bible/stories/3')
      ]);
      
      setStories({
        1: results[0].data.stories || [],
        2: results[1].data.stories || [],
        3: results[2].data.stories || []
      });
    } catch (error) {
      console.error('Failed to fetch Bible stories:', error);
      toast.error('Failed to load Bible stories');
    } finally {
      setLoading(false);
    }
  };

  const fetchChapters = async () => {
    try {
      const response = await api.get('/bible/chapters');
      setChapters(response.data.chapters || []);
    } catch (error) {
      console.error('Failed to fetch chapters:', error);
    }
  };

  // Filter stories by search
  const filteredStories = stories[activeLevel]?.filter(story => 
    !searchQuery || 
    story.title?.toLowerCase().includes(searchQuery.toLowerCase()) ||
    story.summary?.toLowerCase().includes(searchQuery.toLowerCase()) ||
    story.reference?.toLowerCase().includes(searchQuery.toLowerCase())
  ) || [];

  // Group stories by category
  const groupedStories = filteredStories.reduce((acc, story) => {
    let category = 'Other Stories';
    const id = story.id?.toLowerCase() || '';
    
    if (id.includes('jesus') || id.includes('parable') || id.includes('miracle') || id.includes('sermon') || id.includes('disciple')) {
      category = 'Jesus & New Testament';
    } else if (id.includes('abraham') || id.includes('isaac') || id.includes('jacob') || id.includes('joseph')) {
      category = 'The Patriarchs';
    } else if (id.includes('moses') || id.includes('exodus') || id.includes('plague') || id.includes('red-sea') || id.includes('commandment')) {
      category = 'Moses & Exodus';
    } else if (id.includes('david') || id.includes('solomon') || id.includes('saul')) {
      category = 'Kings of Israel';
    } else if (id.includes('elijah') || id.includes('elisha') || id.includes('isaiah') || id.includes('jeremiah') || id.includes('daniel') || id.includes('ezekiel')) {
      category = 'Prophets';
    } else if (id.includes('samson') || id.includes('deborah') || id.includes('gideon') || id.includes('ruth')) {
      category = 'Judges & Heroes';
    } else if (id.includes('esther') || id.includes('nehemiah') || id.includes('job')) {
      category = 'Faith & Courage';
    } else if (id.includes('creation') || id.includes('noah') || id.includes('adam') || id.includes('babel')) {
      category = 'Creation & Early Stories';
    } else if (id.includes('resurrection') || id.includes('pentecost') || id.includes('paul') || id.includes('commission')) {
      category = 'Resurrection & Early Church';
    } else if (id.includes('joshua') || id.includes('jericho')) {
      category = 'Joshua & Promised Land';
    }
    
    if (!acc[category]) acc[category] = [];
    acc[category].push(story);
    return acc;
  }, {});

  const levelConfig = LEVEL_CONFIG[activeLevel];
  const LevelIcon = levelConfig?.icon || Book;

  if (loading) {
    return (
      <div className="min-h-screen bg-gradient-to-b from-purple-50 to-pink-50 flex items-center justify-center">
        <div className="text-center">
          <Loader2 className="w-12 h-12 animate-spin text-purple-600 mx-auto mb-4" />
          <p className="text-purple-600 font-medium">Loading Bible Stories...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gradient-to-b from-purple-50 to-pink-50">
      {/* Header */}
      <header className="bg-white/80 backdrop-blur-md border-b border-purple-200 sticky top-0 z-50">
        <div className="max-w-6xl mx-auto px-4 py-4">
          <div className="flex items-center justify-between">
            <Link to={`/learn/${childId}`}>
              <Button variant="ghost" size="icon" className="rounded-full">
                <ArrowLeft className="w-5 h-5" />
              </Button>
            </Link>
            <div className="flex items-center gap-2">
              <Heart className="w-6 h-6 text-pink-500" />
              <h1 className="font-bold text-xl text-purple-800">Bible Stories</h1>
            </div>
            <Badge variant="outline" className="bg-purple-100 text-purple-700">
              {stories[1].length + stories[2].length + stories[3].length} Stories
            </Badge>
          </div>
        </div>
      </header>

      <main className="max-w-6xl mx-auto px-4 py-6">
        {/* Intro */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          className="text-center mb-6"
        >
          <div className="text-5xl mb-3">📖</div>
          <h1 className="font-bold text-2xl text-purple-800 mb-2">
            Explore God's Word
          </h1>
          <p className="text-purple-600">
            Discover amazing stories from the Bible
          </p>
        </motion.div>

        {/* Full Chapter Readings Section */}
        {chapters.length > 0 && (
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.1 }}
            className="mb-8"
          >
            <Card className="bg-gradient-to-r from-indigo-100 to-purple-100 border-indigo-200">
              <CardContent className="p-4">
                <div className="flex items-center justify-between mb-3">
                  <div className="flex items-center gap-3">
                    <div className="w-10 h-10 bg-indigo-200 rounded-full flex items-center justify-center">
                      <Book className="w-5 h-5 text-indigo-600" />
                    </div>
                    <div>
                      <h3 className="font-bold text-indigo-800">Full Chapter Readings</h3>
                      <p className="text-sm text-indigo-600">Read complete Bible chapters with kid-friendly versions</p>
                    </div>
                  </div>
                  <Button
                    variant="outline"
                    size="sm"
                    onClick={() => setShowChapters(!showChapters)}
                    className="border-indigo-300 text-indigo-600"
                    data-testid="toggle-chapters-btn"
                  >
                    {showChapters ? 'Hide' : 'Show'} Chapters
                  </Button>
                </div>
                
                <AnimatePresence>
                  {showChapters && (
                    <motion.div
                      initial={{ opacity: 0, height: 0 }}
                      animate={{ opacity: 1, height: 'auto' }}
                      exit={{ opacity: 0, height: 0 }}
                      className="grid sm:grid-cols-2 lg:grid-cols-3 gap-3 mt-4"
                    >
                      {chapters.map(chapter => (
                        <Link 
                          key={chapter.id} 
                          to={`/bible-chapter/${chapter.id}?child=${childId}`}
                          className="block"
                        >
                          <Card className="hover:shadow-md transition-shadow cursor-pointer bg-white border-indigo-100 hover:border-indigo-300">
                            <CardContent className="p-3">
                              <div className="flex items-start gap-2">
                                <span className="text-2xl">{getStoryEmoji(chapter.id)}</span>
                                <div className="flex-1 min-w-0">
                                  <h4 className="font-semibold text-sm text-slate-800 truncate">{chapter.title}</h4>
                                  <p className="text-xs text-indigo-600">{chapter.book} {chapter.chapter}</p>
                                  <p className="text-xs text-slate-500 mt-1">{chapter.verses_count} verses • {chapter.theme}</p>
                                </div>
                                <ChevronRight className="w-4 h-4 text-indigo-400 flex-shrink-0 mt-1" />
                              </div>
                            </CardContent>
                          </Card>
                        </Link>
                      ))}
                    </motion.div>
                  )}
                </AnimatePresence>
              </CardContent>
            </Card>
          </motion.div>
        )}

        {/* Level Tabs */}
        <Tabs value={activeLevel} onValueChange={setActiveLevel} className="mb-6">
          <TabsList className="grid grid-cols-3 w-full max-w-lg mx-auto h-auto p-1">
            {Object.entries(LEVEL_CONFIG).map(([level, config]) => {
              const Icon = config.icon;
              return (
                <TabsTrigger 
                  key={level} 
                  value={level}
                  className="flex flex-col py-3 data-[state=active]:bg-white"
                  data-testid={`level-tab-${level}`}
                >
                  <Icon className="w-5 h-5 mb-1" />
                  <span className="text-xs font-medium">{config.name}</span>
                  <span className="text-xs text-slate-500">Ages {config.ages}</span>
                </TabsTrigger>
              );
            })}
          </TabsList>
        </Tabs>

        {/* Search */}
        <div className="mb-6 max-w-md mx-auto">
          <div className="relative">
            <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 w-4 h-4 text-slate-400" />
            <Input
              placeholder="Search stories..."
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              className="pl-10"
              data-testid="story-search"
            />
          </div>
        </div>

        {/* Stats Bar */}
        <div className={`rounded-xl p-4 mb-6 ${levelConfig.bgColor}`}>
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-3">
              <LevelIcon className="w-8 h-8 text-purple-600" />
              <div>
                <h3 className="font-bold text-purple-800">{levelConfig.name}</h3>
                <p className="text-sm text-purple-600">Ages {levelConfig.ages}</p>
              </div>
            </div>
            <div className="text-right">
              <p className="text-2xl font-bold text-purple-800">{filteredStories.length}</p>
              <p className="text-xs text-purple-600">Stories Available</p>
            </div>
          </div>
        </div>

        {/* Stories by Category */}
        <AnimatePresence mode="wait">
          <motion.div
            key={activeLevel + searchQuery}
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
          >
            {Object.entries(groupedStories).map(([category, categoryStories], catIdx) => (
              <div key={category} className="mb-8">
                <h2 className="font-bold text-lg text-slate-700 mb-4 flex items-center gap-2">
                  <span className="w-2 h-2 rounded-full bg-purple-500"></span>
                  {category}
                  <Badge variant="secondary" className="ml-2">{categoryStories.length}</Badge>
                </h2>
                
                <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-4">
                  {categoryStories.map((story, idx) => (
                    <motion.div
                      key={story.id}
                      initial={{ opacity: 0, y: 10 }}
                      animate={{ opacity: 1, y: 0 }}
                      transition={{ delay: idx * 0.03 }}
                    >
                      <Link to={`/bible-story/${story.id}?child=${childId}`}>
                        <Card className="hover:shadow-lg transition-all cursor-pointer border-2 border-purple-100 hover:border-purple-300 group h-full">
                          <CardContent className="p-4">
                            <div className="flex items-start gap-3">
                              <div className="text-3xl group-hover:scale-110 transition-transform flex-shrink-0">
                                {getStoryEmoji(story.id)}
                              </div>
                              <div className="flex-1 min-w-0">
                                <h3 className="font-bold text-slate-800 mb-1 line-clamp-1">
                                  {story.title}
                                </h3>
                                <p className="text-xs text-purple-600 mb-2">
                                  {story.reference}
                                </p>
                                <p className="text-sm text-slate-600 line-clamp-2 mb-2">
                                  {story.summary}
                                </p>
                                <div className="flex items-center justify-between">
                                  <Badge variant="outline" className="text-xs bg-amber-50 text-amber-700 border-amber-200">
                                    {story.lesson?.split(' ').slice(0, 4).join(' ')}...
                                  </Badge>
                                  <ChevronRight className="w-4 h-4 text-purple-400 group-hover:translate-x-1 transition-transform" />
                                </div>
                              </div>
                            </div>
                          </CardContent>
                        </Card>
                      </Link>
                    </motion.div>
                  ))}
                </div>
              </div>
            ))}

            {filteredStories.length === 0 && (
              <div className="text-center py-12">
                <div className="text-4xl mb-4">🔍</div>
                <p className="text-slate-600">No stories found matching your search.</p>
                <Button 
                  variant="link" 
                  onClick={() => setSearchQuery('')}
                  className="mt-2"
                >
                  Clear search
                </Button>
              </div>
            )}
          </motion.div>
        </AnimatePresence>

        {/* How it works */}
        <Card className="mt-8 bg-gradient-to-r from-purple-100 to-pink-100 border-purple-200">
          <CardContent className="p-6">
            <div className="flex items-center gap-4">
              <div className="w-12 h-12 bg-white rounded-full flex items-center justify-center flex-shrink-0">
                <Star className="w-6 h-6 text-yellow-500" />
              </div>
              <div>
                <h4 className="font-bold text-purple-800">How it works</h4>
                <p className="text-sm text-purple-600">
                  Each story includes the Bible reference, a summary, and an important lesson. 
                  Read the story, learn the lesson, and grow in your faith!
                </p>
              </div>
            </div>
          </CardContent>
        </Card>
      </main>
    </div>
  );
};

export default BibleStoriesPage;
